// apps/client/src/store/gameStore.ts
//
// Game state store using Zustand
// Includes dual independent timers (Daily + Sunday) per PDR
//

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';
import { socketClient } from '../lib/socket';

interface Ticket {
    id: string;
    sessionId: string;
    grid: (number | null)[][];
    hits: number[];
    status: string;
    gameType?: string | null;
}

// Compute seconds until next Sunday 20:00 UTC
function getSecondsUntilSunday(): number {
    const now = new Date();
    const dayOfWeek = now.getUTCDay(); // 0=Sunday
    let daysUntil = (7 - dayOfWeek) % 7;
    if (daysUntil === 0) {
        // It's Sunday — check if 20:00 UTC already passed
        const sundayTarget = new Date(now);
        sundayTarget.setUTCHours(20, 0, 0, 0);
        if (now >= sundayTarget) {
            daysUntil = 7; // next Sunday
        }
    }
    const target = new Date(now);
    target.setUTCDate(now.getUTCDate() + daysUntil);
    target.setUTCHours(20, 0, 0, 0);
    return Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
}

interface GameState {
    // Session state
    sessionId: string | null;
    status: 'WAITING' | 'PLAYING' | 'FINISHED' | 'NO_SESSION' | 'LOADING';
    type: string | null;
    timeUntilStart: number | null;
    ballsDrawn: number;
    totalBalls: number;
    drawnNumbers: number[];
    playerCount: number;
    lineAwarded: boolean;
    twoLinesAwarded: boolean;

    // My tickets
    tickets: Ticket[];
    lastDrawnNumber: number | null;

    // Dual timers (PDR 1.3)
    dailyTimer: number;   // seconds until next 15-min game
    sundayTimer: number;  // seconds until Sunday 20:00 UTC
    _timerInterval: ReturnType<typeof setInterval> | null;

    // Jackpot
    jackpotAmount: number; // real value from backend (0 = not loaded)

    // Prize config (from server settings)
    sundayPrizePool: number;
    dailyLinePrize: number;
    dailyTwoLinesPrize: number;
    dailyBingoPrize: number;

    // Card acquisition (persists across navigation)
    earnedCards: number;
    bundleProgress: number;
    bundleUsedThisSession: boolean;
    _lastSessionId: string | null; // track session changes to reset bundle

    // UI state
    isConnected: boolean;
    error: string | null;

    // Actions
    fetchGameState: () => Promise<void>;
    buyTicket: (payWithChips?: boolean) => Promise<Ticket | null>;
    fetchMyTickets: () => Promise<void>;
    joinGame: (userId: string) => Promise<void>;
    leaveGame: () => void;
    addDrawnNumber: (number: number, allNumbers: number[]) => void;
    setGameStarted: (sessionId: string) => void;
    setGameEnded: () => void;
    updateTicketHits: () => void;
    startTimerTicks: () => void;
    stopTimerTicks: () => void;
    addEarnedCards: (count: number) => void;
    resetEarnedCards: () => void;
    setBundleProgress: (progress: number) => void;
    markBundleUsed: () => void;
    setPhaseAwarded: (phase: 'LINE' | 'TWO_LINES') => void;
}

export const useGameStore = create<GameState>()(
    persist(
        (set, get) => ({
            // Initial state
            sessionId: null,
            status: 'LOADING',
            type: null,
            timeUntilStart: null,
            ballsDrawn: 0,
            totalBalls: 90,
            drawnNumbers: [],
            playerCount: 0,
            lineAwarded: false,
            twoLinesAwarded: false,

            // My tickets
            tickets: [],
            lastDrawnNumber: null,
            isConnected: false,
            error: null,

            // Dual timers
            dailyTimer: 0,
            sundayTimer: getSecondsUntilSunday(),
            _timerInterval: null,

            // Jackpot
            jackpotAmount: 0,

            // Prize config (from server settings)
            sundayPrizePool: 10000,
            dailyLinePrize: 500,
            dailyTwoLinesPrize: 1000,
            dailyBingoPrize: 2000,

            // Card acquisition (persists across navigation)
            earnedCards: 0,
            bundleProgress: 0,
            bundleUsedThisSession: false,
            _lastSessionId: null,

            // Start ticking both timers every second
            startTimerTicks: () => {
                const existing = get()._timerInterval;
                if (existing) return; // already running

                const interval = setInterval(() => {
                    set((state) => ({
                        dailyTimer: Math.max(0, state.dailyTimer - 1),
                        sundayTimer: Math.max(0, state.sundayTimer - 1),
                    }));
                }, 1000);

                set({ _timerInterval: interval });
            },

            stopTimerTicks: () => {
                const interval = get()._timerInterval;
                if (interval) {
                    clearInterval(interval);
                    set({ _timerInterval: null });
                }
            },

            // Fetch current game state from API
            fetchGameState: async () => {
                try {
                    const response = await api.getGameState();

                    if (response.ok && response.state) {
                        const s = response.state;
                        const prevSessionId = get()._lastSessionId;
                        const isNewSession = s.sessionId !== null && s.sessionId !== prevSessionId;

                        set({
                            sessionId: s.sessionId,
                            status: s.status as GameState['status'],
                            type: s.type,
                            timeUntilStart: s.timeUntilStart,
                            ballsDrawn: s.ballsDrawn,
                            totalBalls: s.totalBalls,
                            drawnNumbers: s.drawnNumbers,
                            // Only overwrite playerCount from HTTP if the server
                            // actually returned a positive value. Otherwise, keep
                            // the real-time value we already got from WebSocket
                            // (player_count_updated or player_joined events).
                            // This prevents the 5s polling from resetting the
                            // count back to 0 right after a ticket purchase.
                            ...(s.playerCount > 0 ? { playerCount: s.playerCount } : {}),
                            lineAwarded: s.lineAwarded ?? false,
                            twoLinesAwarded: s.twoLinesAwarded ?? false,
                            error: null,
                            // Sync daily timer from server (only if it's a daily game, otherwise fallback to local estimate or 0)
                            dailyTimer: s.type === 'DAILY_15' ? (s.timeUntilStart ?? 0) : 0,
                            // Fix: Recompute sunday timer, respecting live active Sunday sessions
                            // If backend says Sunday Gala is active, sync with backend time. Otherwise rely on strict local calc to next Sunday.
                            sundayTimer: (s.type === 'SUNDAY_GALA' && s.status !== 'FINISHED')
                                ? (s.timeUntilStart ?? 0)
                                : getSecondsUntilSunday(),
                            // Prize config from settings
                            sundayPrizePool: s.sundayPrizePool ?? 10000,
                            dailyLinePrize: s.dailyLinePrize ?? 500,
                            dailyTwoLinesPrize: s.dailyTwoLinesPrize ?? 1000,
                            dailyBingoPrize: s.dailyBingoPrize ?? 2000,
                            // Track session for bundle reset (Bug #2)
                            _lastSessionId: s.sessionId,
                            // Reset bundle when session changes
                            ...(isNewSession ? {
                                bundleUsedThisSession: false,
                                bundleProgress: 0,
                                earnedCards: 0,
                            } : {}),
                        });
                    }
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to fetch game state';
                    set({ error: message, status: 'NO_SESSION' });
                }
            },

            // Buy a ticket for current session
            buyTicket: async (payWithChips = false) => {
                try {
                    const response = await api.buyTicket('DAILY_15', payWithChips) as any;

                    // Support both response shapes: { ticket } (singular) and { allTickets } (array)
                    const rawTicket = response.ticket ?? response.allTickets?.[0] ?? response.tickets?.[0];

                    if (response.ok && rawTicket) {
                        const ticket: Ticket = {
                            id: rawTicket.id,
                            sessionId: rawTicket.sessionId,
                            grid: rawTicket.grid,
                            hits: [],
                            status: rawTicket.status,
                        };

                        // If x2 multiplier gave extra tickets, add all of them
                        const extraTickets = (response.allTickets ?? response.tickets ?? []).slice(1);
                        const allNew = [ticket, ...extraTickets.map((t: any) => ({
                            id: t.id,
                            sessionId: t.sessionId,
                            grid: t.grid,
                            hits: [],
                            status: t.status,
                        }))].filter((t: Ticket) => t.id); // filter out undefined

                        set((state) => ({
                            tickets: [...state.tickets, ...allNew],
                            error: null,
                        }));

                        return ticket;
                    }

                    console.error('❌ buyTicket: response not ok', response);
                    throw new Error(response?.message || 'Failed to buy ticket');
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Failed to buy ticket';
                    console.error('❌ buyTicket error:', message, error);
                    set({ error: message });
                    return null;
                }
            },

            // Fetch user's active daily tickets (filters out Sunday tickets)
            fetchMyTickets: async () => {
                try {
                    const response = await api.getMyTickets();

                    if (response.ok) {
                        // Only keep DAILY_15 tickets (or null gameType for backwards compat)
                        const dailyTickets = response.tickets.filter(
                            (t) => !t.gameType || t.gameType === 'DAILY_15'
                        );
                        set({
                            tickets: dailyTickets.map((t) => ({
                                id: t.id,
                                sessionId: t.sessionId,
                                grid: t.grid,
                                hits: t.hits,
                                status: t.status,
                                gameType: t.gameType,
                            })),
                        });
                    }
                } catch (error) {
                    console.error('Failed to fetch tickets:', error);
                }
            },

            // Join game session via WebSocket
            joinGame: async (userId: string) => {
                const { sessionId } = get();
                if (!sessionId) return;

                const socket = socketClient.connect();

                // Track connection state immediately
                if (socket) {
                    socket.on('connect', () => set({ isConnected: true }));
                    socket.on('disconnect', () => set({ isConnected: false }));
                }

                // CRITICAL: Wait for socket to actually connect before joining room
                const connected = await socketClient.waitForConnection();
                if (!connected) {
                    console.warn('⚠️ Socket failed to connect — cannot join game room');
                    set({ isConnected: false });
                    return;
                }

                // Set up event listeners (AFTER connection confirmed)
                socketClient.onNumberDrawn(({ number, drawnNumbers }) => {
                    get().addDrawnNumber(number, drawnNumbers);
                });

                socketClient.onGameStarted(({ sessionId: newSessionId }) => {
                    get().setGameStarted(newSessionId);
                });

                socketClient.onGameEnded(() => {
                    get().setGameEnded();
                });

                socketClient.onPlayerJoined(({ roomCount }) => {
                    set({ playerCount: roomCount });
                });

                socketClient.onPlayerLeft(({ roomCount }) => {
                    set({ playerCount: roomCount });
                });

                // Real-time player count from server after ticket purchase
                // (more accurate than player_joined which counts WS connections, not unique ticket holders)
                socketClient.onPlayerCountUpdated(({ playerCount }) => {
                    set({ playerCount });
                });

                // Join the room (socket is confirmed connected at this point)
                const result = await socketClient.joinRoom(sessionId, userId);
                set({ isConnected: result.success });
            },

            // Leave game session
            leaveGame: () => {
                const { sessionId } = get();
                if (sessionId) {
                    socketClient.leaveRoom(sessionId);
                }
                socketClient.removeAllListeners();
                socketClient.disconnect();
                set({ isConnected: false });
            },

            // Add a drawn number
            addDrawnNumber: (number: number, allNumbers: number[]) => {
                set({
                    lastDrawnNumber: number,
                    drawnNumbers: allNumbers,
                    ballsDrawn: allNumbers.length,
                });

                // Update ticket hits
                get().updateTicketHits();
            },

            // Update ticket hits based on drawn numbers
            updateTicketHits: () => {
                const { drawnNumbers, tickets } = get();
                const drawnSet = new Set(drawnNumbers);

                const updatedTickets = tickets.map((ticket) => {
                    const allNumbers = ticket.grid
                        .flat()
                        .filter((n): n is number => n !== null);
                    const hits = allNumbers.filter((n) => drawnSet.has(n));
                    return { ...ticket, hits };
                });

                set({ tickets: updatedTickets });
            },

            // Set game started
            setGameStarted: (sessionId: string) => {
                set({
                    sessionId,
                    status: 'PLAYING',
                    drawnNumbers: [],
                    ballsDrawn: 0,
                    lastDrawnNumber: null,
                });
            },

            // Set game ended — reset card acquisition for next session
            setGameEnded: () => {
                set({
                    status: 'FINISHED',
                    earnedCards: 0,
                    bundleProgress: 0,
                    bundleUsedThisSession: false,
                });
                // Fetch new state after a short delay
                setTimeout(() => {
                    get().fetchGameState();
                }, 2000);
            },

            // Card acquisition actions
            addEarnedCards: (count: number) => {
                set((state) => ({ earnedCards: state.earnedCards + count }));
            },
            resetEarnedCards: () => {
                set({ earnedCards: 0 });
            },
            setBundleProgress: (progress: number) => {
                set({ bundleProgress: progress });
            },
            markBundleUsed: () => {
                set({ bundleUsedThisSession: true });
            },

            setPhaseAwarded: (phase) => {
                if (phase === 'LINE') set({ lineAwarded: true });
                if (phase === 'TWO_LINES') set({ twoLinesAwarded: true });
            }
        }),
        {
            name: 'bingoton-game-store',
            partialize: (state) => ({
                bundleProgress: state.bundleProgress,
                bundleUsedThisSession: state.bundleUsedThisSession,
                _lastSessionId: state._lastSessionId,
            }),
        }
    )
);
